import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
  extractGoogleDriveFileId,
  getGoogleDriveImageCandidates,
  useAdBanners,
} from '../../hooks/useAdBanners';
import './AdBanner.css';

const publicUrl = process.env.PUBLIC_URL || '';

const FALLBACK_BANNERS = {
  banner_1: [
    `${publicUrl}/images/ads_marketplace/ad_banner_home.png`,
    `${publicUrl}/images/ads_marketplace/ad_banner_seller_features.png`,
    `${publicUrl}/images/ads_marketplace/ad_banner_contact_support.png`,
  ],
  banner_2: [`${publicUrl}/images/ads_marketplace/ad_banner_freeship.png`],
  banner_3: [`${publicUrl}/images/ads_marketplace/ad_banner_promo.png`],
};

const MAIN_SLIDER_SETTINGS = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 5000,
  pauseOnHover: true,
  arrows: false,
};

const SIDE_SLIDER_SETTINGS = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 6000,
  pauseOnHover: true,
  arrows: false,
  fade: true,
};

function resolveBannerList(sheetImages, fallbackImages) {
  return sheetImages.length > 0 ? sheetImages : fallbackImages;
}

function getImageCandidates(src) {
  const fileId = extractGoogleDriveFileId(src);
  if (fileId) {
    return getGoogleDriveImageCandidates(fileId);
  }
  return [src];
}

function BannerImage({ src, alt }) {
  const candidates = useMemo(() => getImageCandidates(src), [src]);
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [src]);

  if (candidates.length === 0) return null;

  const handleError = () => {
    setCandidateIndex((prev) => (prev < candidates.length - 1 ? prev + 1 : prev));
  };

  return (
    <img
      src={candidates[candidateIndex]}
      alt={alt}
      className='ad-banner-img'
      referrerPolicy='no-referrer'
      onError={handleError}
    />
  );
}

function BannerSlider({ images, settings, className, altPrefix, sliderKey }) {
  if (images.length === 0) return null;

  if (images.length === 1) {
    return (
      <div className={className}>
        <BannerImage src={images[0]} alt={`${altPrefix} 1`} />
      </div>
    );
  }

  return (
    <Slider {...settings} key={sliderKey} className={className}>
      {images.map((src, index) => (
        <div key={`${sliderKey}-${index}`} className='ad-slide-item'>
          <BannerImage src={src} alt={`${altPrefix} ${index + 1}`} />
        </div>
      ))}
    </Slider>
  );
}

BannerImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
};

BannerSlider.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  settings: PropTypes.object.isRequired,
  className: PropTypes.string,
  altPrefix: PropTypes.string,
  sliderKey: PropTypes.string.isRequired,
};

function AdBanner() {
  const { banners, loading } = useAdBanners();

  const bannerImages = useMemo(
    () => ({
      banner_1: resolveBannerList(banners.banner_1, FALLBACK_BANNERS.banner_1),
      banner_2: resolveBannerList(banners.banner_2, FALLBACK_BANNERS.banner_2),
      banner_3: resolveBannerList(banners.banner_3, FALLBACK_BANNERS.banner_3),
    }),
    [banners],
  );

  const sliderKeys = useMemo(
    () => ({
      banner_1: bannerImages.banner_1.join('|'),
      banner_2: bannerImages.banner_2.join('|'),
      banner_3: bannerImages.banner_3.join('|'),
    }),
    [bannerImages],
  );

  if (loading) {
    return (
      <div className='ad-banner-wrapper'>
        <div className='container'>
          <div className='ad-grid-container is-loading'>
            <div className='ad-main-slider shadow-sm banner-skeleton' />
            <div className='ad-side-banners'>
              <div className='ad-side-item shadow-sm banner-skeleton' />
              <div className='ad-side-item shadow-sm banner-skeleton' />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='ad-banner-wrapper'>
      <div className='container'>
        <div className='ad-grid-container'>
          <div className='ad-main-slider shadow-sm'>
            <BannerSlider
              images={bannerImages.banner_1}
              settings={MAIN_SLIDER_SETTINGS}
              className='modern-ad-slider'
              altPrefix='โฆษณาหลัก'
              sliderKey={`main-${sliderKeys.banner_1}`}
            />
          </div>

          <div className='ad-side-banners'>
            <div className='ad-side-item shadow-sm'>
              <BannerSlider
                images={bannerImages.banner_2}
                settings={SIDE_SLIDER_SETTINGS}
                className='ad-side-slider'
                altPrefix='โฆษณารอง 1'
                sliderKey={`side2-${sliderKeys.banner_2}`}
              />
            </div>
            <div className='ad-side-item shadow-sm'>
              <BannerSlider
                images={bannerImages.banner_3}
                settings={SIDE_SLIDER_SETTINGS}
                className='ad-side-slider'
                altPrefix='โฆษณารอง 2'
                sliderKey={`side3-${sliderKeys.banner_3}`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdBanner;
